'use client';

import { Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import Cookies from 'js-cookie';
import React, { useEffect, useLayoutEffect, useState } from 'react';

import { inter } from '@/assets/fonts';
import Loading from '@/components/Loading';
import type { ThemeSchemaType } from '@/global/schemas/theme.schema';
import { FontConfig, loadTypography } from '@/global/styles/fonts';
import { ThemeConfig, ThemeMode, createCustomTheme } from '@/global/styles/theme';
import type { TypographyConfig } from '@/global/styles/theme';
import { NextMuiProvider, Web3BalanceProvider } from '@/providers';
import { setNodeVersion } from '@/store/actions/app.action';
import { useFetchProjectThemeV2Query } from '@/store/services/project.service';
import { useAppDispatch } from '@/store/utils';

interface ThemeProviderProps {
  nodeId: string;
  children: React.ReactNode;
}

interface Typography {
  className: string;
  styles: any;
  customFonts: FontConfig[];
}

interface StoreThemeConfig {
  config: ThemeConfig;
  fontClassName: string;
  typography: Typography;
}

interface ThemeState {
  theme: Theme;
  fontClassName: string;
  typography: Typography | null;
  isInitializing: boolean;
}

const getStoredTheme = (nodeId: string): StoreThemeConfig | null => {
  if (typeof window === 'undefined') return null;

  try {
    const storedTheme = sessionStorage.getItem(nodeId);
    return storedTheme ? JSON.parse(storedTheme) : null;
  } catch (error) {
    return null;
  }
};

const DEFAULT_THEME = createCustomTheme({} as ThemeConfig);

const ThemeProvider = ({ children, nodeId }: ThemeProviderProps) => {
  const dispatch = useAppDispatch();
  const [mounted, setMounted] = useState(false);
  const [themeState, setThemeState] = useState<ThemeState>({
    theme: DEFAULT_THEME,
    fontClassName: '',
    typography: null,
    isInitializing: true, // Start with true always for SSR consistency
  });

  const [storedTheme, setStoredTheme] = useState<StoreThemeConfig | null>(null);

  const { data: themeRes, isLoading } = useFetchProjectThemeV2Query({ nodeId }, { skip: !nodeId || !!storedTheme });

  // Handle client-side initialization
  useEffect(() => {
    setMounted(true);
    const theme = getStoredTheme(nodeId);
    setStoredTheme(theme);
    const version = Cookies.get(`version-${nodeId}`);
    if (theme && version) {
      dispatch(setNodeVersion(JSON.parse(version) === '2' ? 'v2' : 'v1'));
      setThemeState({
        theme: createCustomTheme(theme.config),
        fontClassName: theme.fontClassName,
        typography: theme.typography,
        isInitializing: false,
      });
    }
  }, [nodeId]);

  const storeTheme = (data: StoreThemeConfig) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(nodeId, JSON.stringify(data));
    }
  };

  const createTypographyConfig = (themeConfig: ThemeConfig): TypographyConfig => ({
    heading: {
      family: themeConfig.text.heading.fontFamily,
      url: themeConfig.text.heading.fontUrl,
    },
    paragraph: {
      family: themeConfig.text.paragraph.fontFamily,
      url: themeConfig.text.paragraph.fontUrl,
    },
    subText: {
      family: themeConfig.text.subText.fontFamily,
      url: themeConfig.text.subText.fontUrl,
    },
  });

  const updateThemeConfig = (themeConfig: ThemeConfig, typographyResult: Typography): ThemeConfig => ({
    ...themeConfig,
    text: {
      ...themeConfig.text,
      heading: {
        ...themeConfig.text.heading,
        fontFamily: typographyResult.styles.heading?.fontFamily || typographyResult.styles.fontFamily || 'Inter',
      },
      paragraph: {
        ...themeConfig.text.paragraph,
        fontFamily: typographyResult.styles.paragraph?.fontFamily || typographyResult.styles.fontFamily || 'Inter',
      },
      subText: {
        ...themeConfig.text.subText,
        fontFamily: typographyResult.styles.subText?.fontFamily || typographyResult.styles.fontFamily || 'Inter',
      },
    },
  });

  const getFontClassName = (typographyResult: Typography): string => {
    return typographyResult.className === 'multiple-fonts'
      ? `${typographyResult.styles.heading?.className} ${typographyResult.styles.paragraph?.className} ${typographyResult.styles.subText?.className}`
      : typographyResult.className;
  };

  const initializeThemeWithConfig = async (themeConfig: ThemeConfig) => {
    try {
      const typographyConfig = createTypographyConfig(themeConfig);
      const typographyResult = await loadTypography(typographyConfig);
      const updatedThemeConfig = updateThemeConfig(themeConfig, typographyResult);
      const customFontClassname = getFontClassName(typographyResult);
      const customTheme = createCustomTheme(updatedThemeConfig);

      const storeData: StoreThemeConfig = {
        config: updatedThemeConfig,
        fontClassName: customFontClassname,
        typography: typographyResult,
      };

      setThemeState({
        theme: customTheme,
        fontClassName: customFontClassname,
        typography: typographyResult,
        isInitializing: false,
      });
      storeTheme(storeData);
      setStoredTheme(storeData);
    } catch (error) {
      console.error('Failed to initialize theme:', error);
      setThemeState(prev => ({ ...prev, isInitializing: false }));
      throw error;
    }
  };

  useLayoutEffect(() => {
    if (!mounted) return;

    const initializeTheme = async (theme: ThemeSchemaType) => {
      const { version, components, modeTheme, ...rest } = theme;
      Cookies.set(`version-${nodeId}`, JSON.stringify(version), { sameSite: 'strict', expires: 365 * 100 });
      dispatch(setNodeVersion(version === '2' ? 'v2' : 'v1'));

      const themeConfig = {
        mode: modeTheme as ThemeMode,
        components,
        ...rest,
      };

      await initializeThemeWithConfig(themeConfig);
    };

    const loadTheme = async () => {
      if (!storedTheme && !isLoading && themeRes) {
        await initializeTheme(themeRes.data);
      }
    };

    loadTheme();
  }, [nodeId, themeRes, isLoading, mounted, storedTheme]);

  // Show loading state when initializing theme for the first time
  if (!mounted || themeState.isInitializing) {
    return <Loading />;
  }

  return (
    <NextMuiProvider theme={themeState.theme} config={themeState.typography?.customFonts ?? []}>
      <Web3BalanceProvider>
        <Box suppressHydrationWarning className={themeState?.fontClassName ?? inter.className}>
          {children}
        </Box>
      </Web3BalanceProvider>
    </NextMuiProvider>
  );
};

export { ThemeProvider };
